import { confirmDialog } from "./confirmHost.jsx";
import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Coffee, Calendar, Users, RefreshCw, EyeOff, CheckSquare, Square, X, Sun, Moon, Pin, Plane, Plus, Trash2, Undo2, ChevronsUp } from "lucide-react";
import InfoTip from "./InfoTip.jsx";
import DatePicker from "./DatePicker.jsx";

// Sdílená komponenta pro zobrazení "obecného" rozvrhu trenéra.
// Props:
//   data: { workingHours, lunchHour, grid, roaming, sparse, summary, window, absences }
//   editable: boolean - owner / manager / head_coach / trener (pro vlastní)
//   activeClients: [{name, ...}] - pro autocomplete pevného klienta v EditCellModal
//   onCellEdit: async (cell, newType, klient?) → upsert override
//   onCellReset: async (cell) → smaže override
//   onBulkEdit: async (cells, newType) → bulk upsert
//   onAbsenceAdd: async ({dateFrom, dateTo, duvod, poznamka}) → POST nová absence
//   onAbsenceDelete: async (rowNum) → smazat absenci
//   onRefresh: () => void
const DAY_NAMES = { 1: "Pondělí", 2: "Úterý", 3: "Středa", 4: "Čtvrtek", 5: "Pátek" };

const MORNING_END = 12; // dopoledne = před 12:00
const AFTERNOON_START = 14; // odpoledne = od 14:00 (mezi je oběd)

export default function RozvrhVzorGrid({ data, jenCteni = false, editable = false, canFixClient = false, canEditAbsence = false, canBackdateAbsence = false, minAbsenceDaysAhead = 0, activeClients = [], onCellEdit, onCellReset, onBulkEdit, onAbsenceAdd, onAbsenceDelete, onRefresh, refreshing = false, onUndo, undoCount = 0, undoLabel = "" }) {
  const [editingCell, setEditingCell] = useState(null);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedCells, setSelectedCells] = useState(new Set());
  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [showHiddenDays, setShowHiddenDays] = useState(false);
  // Compact lunch bar - kterou hodinu uživatel rozbalil zpět na 5 jednotlivých buněk
  const [expandedHours, setExpandedHours] = useState(new Set());
  const toggleExpandedHour = (h) => {
    setExpandedHours((prev) => {
      const next = new Set(prev);
      if (next.has(h)) next.delete(h); else next.add(h);
      return next;
    });
  };

  // 6.8.2026 fix React #310: zadny early return pred hooky — `data` dorazi az
  // asynchronne, takze `if (!data) return null` menil pocet zavolanych hooku
  // mezi rendery. Guard je az tesne pred hlavnim JSX returnem nize.
  const { workingHours = { start: 6, end: 20 }, grid = [], roaming = [], sparse = [], summary, window, absences = [] } = data || {};

  const hours = useMemo(() => {
    const out = [];
    for (let h = workingHours.start; h < workingHours.end; h++) out.push(h);
    return out;
  }, [workingHours]);

  const cellMap = useMemo(() => {
    const m = new Map();
    for (const cell of grid) m.set(`${cell.dow}-${cell.hour}`, cell);
    return m;
  }, [grid]);

  // Roaming overlay map - pro každou (dow, hour) buňku seznam putujících klientů
  // co se tam pravidelně vyskytují (≥30% jejich bookings v té buňce). Sales je vidí
  // v slot karte jako "tady cca chodí".
  const roamingByCell = useMemo(() => {
    const m = new Map(); // "dow-hour" → [{ klient, ratio, tydneFreq }]
    const allRoaming = [...roaming, ...sparse];
    for (const r of allRoaming) {
      if (!Array.isArray(r.cellHits)) continue;
      for (const hit of r.cellHits) {
        const key = `${hit.dow}-${hit.hour}`;
        if (!m.has(key)) m.set(key, []);
        m.get(key).push({ klient: r.klient, ratio: hit.ratio, tydneFreq: r.tydneFreq });
      }
    }
    // Seřaď v každé buňce podle ratio sestupně, max 2 zobrazí SlotCell
    for (const list of m.values()) list.sort((a, b) => b.ratio - a.ratio);
    return m;
  }, [roaming, sparse]);

  // Sloupce dne: skryj sloupec pokud všechny buňky toho dne jsou type="hidden"
  // (trenér označil celý den jako mimo pracovní dobu - nemá smysl ho zobrazit).
  // Toggle showHiddenDays vrátí všechny zpět (ať je možné editovat).
  const allDays = [1, 2, 3, 4, 5];
  const hiddenDays = useMemo(() => {
    return allDays.filter((d) => {
      const cells = grid.filter((c) => c.dow === d);
      if (cells.length === 0) return false;
      return cells.every((c) => c.type === "hidden");
    });
  }, [grid]);
  const visibleDaysFull = showHiddenDays ? allDays : allDays.filter((d) => !hiddenDays.includes(d));

  // 29.5.2026 v7: Mobile = zobrazí JEN JEDEN DEN s chip pickerem nahoře.
  // 4.6.2026 fix: drive bylo spocitano JEN JEDNOU na mount pres window.innerWidth.
  // Na iOS Safari ale innerWidth pri prvnim renderu obcas vrati desktop hodnotu
  // (layout jeste nesedl / otoceni mobilu) → JS rendroval 5 sloupcu, zatimco CSS
  // (sm:hidden chips) uz byl v mobile rezimu = rozbity dvojsloupcovy pohled.
  // Fix: matchMedia listener drzi JS stav VZDY synchronni s CSS breakpointem.
  const [isMobile, setIsMobile] = useState(() => {
    try {
      if (typeof window === "undefined") return false;
      if (window.matchMedia) return window.matchMedia("(max-width: 767px)").matches;
      return (window.innerWidth || 1024) < 768;
    } catch (_) { return false; }
  });
  useEffect(() => {
    try {
      if (typeof window === "undefined" || !window.matchMedia) return;
      const mq = window.matchMedia("(max-width: 767px)");
      const onChange = (e) => setIsMobile(e.matches);
      // Sync hned (kdyby initial state vznikl pred layoutem) + listener na zmeny.
      setIsMobile(mq.matches);
      if (mq.addEventListener) mq.addEventListener("change", onChange);
      else if (mq.addListener) mq.addListener(onChange);
      return () => {
        if (mq.removeEventListener) mq.removeEventListener("change", onChange);
        else if (mq.removeListener) mq.removeListener(onChange);
      };
    } catch (_) { /* exoticky runtime bez matchMedia — zustane initial */ }
  }, []);
  // Defaultní vybraný den na mobile = dnešní pracovní den (nebo Pondělí).
  const todayDow = useMemo(() => {
    const dow = new Date().getDay(); // 0=Ne, 1=Po, ..., 5=Pá, 6=So
    return dow >= 1 && dow <= 5 ? dow : 1;
  }, []);
  const [mobileSelectedDay, setMobileSelectedDay] = useState(todayDow);
  // Pokud uživatel vybrané dnu na mobile = mezi visibleDaysFull, OK. Jinak fallback na první visible.
  useEffect(() => {
    if (isMobile && !visibleDaysFull.includes(mobileSelectedDay) && visibleDaysFull.length > 0) {
      setMobileSelectedDay(visibleDaysFull[0]);
    }
  }, [isMobile, visibleDaysFull, mobileSelectedDay]);
  // 4.6.2026 v2: mřížka je čistě desktop (mobil má vlastní agendu přes CSS breakpoint),
  // takže grid vždy renderuje všechny dny. isMobile/mobileSelectedDay řídí jen agendu.
  const visibleDays = visibleDaysFull;

  // Tailwind nepodporuje dynamické grid-cols - static mapping podle počtu visibleDays.
  // Mobile: 1 den, sloupec 1fr (vyplní šířku). Desktop: 5 dnů × 140px min.
  const gridColsClass = {
    1: "grid-cols-[90px_minmax(140px,_1fr)] sm:grid-cols-[130px_minmax(140px,_1fr)]",
    2: "grid-cols-[90px_repeat(2,_minmax(110px,_1fr))] sm:grid-cols-[130px_repeat(2,_minmax(140px,_1fr))]",
    3: "grid-cols-[90px_repeat(3,_minmax(110px,_1fr))] sm:grid-cols-[130px_repeat(3,_minmax(140px,_1fr))]",
    4: "grid-cols-[90px_repeat(4,_minmax(110px,_1fr))] sm:grid-cols-[130px_repeat(4,_minmax(140px,_1fr))]",
    5: "grid-cols-[90px_repeat(5,_minmax(110px,_1fr))] sm:grid-cols-[130px_repeat(5,_minmax(140px,_1fr))]",
  }[visibleDays.length] || "grid-cols-[90px_minmax(140px,_1fr)] sm:grid-cols-[130px_repeat(5,_minmax(140px,_1fr))]";
  const minWidthClass = visibleDays.length >= 4 ? "min-w-[850px]" : "min-w-[450px]";

  // Souhrn volna - počítáno z aktuální mřížky
  const capacity = useMemo(() => {
    let totalVolno = 0, morningVolno = 0, afternoonVolno = 0;
    let totalLunch = 0, totalFixed = 0, totalHidden = 0;
    for (const cell of grid) {
      if (cell.type === "volno") {
        totalVolno++;
        if (cell.hour < MORNING_END) morningVolno++;
        else if (cell.hour >= AFTERNOON_START) afternoonVolno++;
      } else if (cell.type === "lunch") totalLunch++;
      else if (cell.type === "hidden") totalHidden++;
      else totalFixed++;
    }
    // Očekávaná kapacita putujících = suma jejich tydneFreq
    const roamingExpected = roaming.reduce((s, r) => s + (r.tydneFreq || 0), 0)
      + sparse.reduce((s, r) => s + (r.tydneFreq || 0), 0);
    const remainingForSales = Math.max(0, totalVolno - Math.round(roamingExpected));
    return { totalVolno, morningVolno, afternoonVolno, totalLunch, totalFixed, totalHidden,
             roamingExpected: Math.round(roamingExpected * 10) / 10, remainingForSales };
  }, [grid, roaming, sparse]);

  const handleCellClick = (cell) => {
    if (!editable) return;
    // V multi-select mode lze vybrat i fixed/merged (jen pokud overridden, jinak ne)
    if (multiSelectMode) {
      const key = `${cell.dow}-${cell.hour}`;
      const next = new Set(selectedCells);
      if (next.has(key)) next.delete(key); else next.add(key);
      setSelectedCells(next);
    } else {
      // 24.8.2026 (hlášení z /feedback): klikatelná je i buňka s pevným klientem
      // z analýzy. Dřív to šlo jen u ručně nastavených, takže když trenér přestal
      // v daném čase pracovat, nedalo se to zadat. Přepnutí na Nepracuje, Volno
      // nebo Oběd teď navíc trenérovi založí úkol klienta přeobjednat.
      setEditingCell(cell);
    }
  };

  const exitMultiSelect = () => { setMultiSelectMode(false); setSelectedCells(new Set()); };

  const handleBulkAction = async (newType) => {
    const cells = [...selectedCells].map((k) => {
      const [dow, hour] = k.split("-").map(Number);
      return { dow, hour };
    });
    if (cells.length === 0) return;
    if (onBulkEdit) await onBulkEdit(cells, newType);
    exitMultiSelect();
  };

  const handleBulkReset = async () => {
    const cells = [...selectedCells].map((k) => {
      const [dow, hour] = k.split("-").map(Number);
      return { dow, hour };
    });
    for (const c of cells) await onCellReset(c);
    exitMultiSelect();
  };

  const formatHour = (h) => `${String(h).padStart(2, "0")}:00 – ${String(h + 1).padStart(2, "0")}:00`;

  const hasRoaming = roaming.length > 0 || sparse.length > 0;

  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* Hlavní layout: na xl+ rozvrh vlevo (3 díly), putující vpravo (1 díl). Pod xl plná šířka.
          29.6.2026 feedback #20: na 13,3" (1280px, lg) zbývalo mřížce s min-w-[850px] jen ~700px
          → horizontální scroll. Boční panel proto až od xl (1280px+ bez sidebaru vedle). */}
      {/* 6. 10. 2026 (Láďa, menší displej): boční panel až od 2xl a s pevnou šířkou, jinak se
          vedle mřížky s min-w-[850px] zmáčkl na pár desítek pixelů a mřížka se ořízla.
          Mřížka má min-w-0, ať se roluje uvnitř své karty, ne pod panel. */}
      <div className={hasRoaming ? "grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_300px] gap-4" : ""}>
      <div className="min-w-0 bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5">
        <h2 data-nadpis-karty className="mb-4 flex w-full min-w-0 items-center gap-3 rounded-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-3 py-2.5 text-white shadow-[0_6px_16px_-10px_rgba(36,20,48,0.9)] sm:gap-2.5 sm:rounded-[14px] sm:px-2.5 sm:py-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20 sm:h-8 sm:w-8">
            <Calendar className="h-5 w-5 sm:h-4 sm:w-4" />
          </span>
          <span className="min-w-0 flex-1 truncate font-serif text-lg font-bold leading-tight">Týdenní vzor</span>
          {editable && undoCount > 0 && (
            <button onClick={onUndo} title={`Vrátit poslední úpravu: ${undoLabel}`} aria-label="Vrátit poslední úpravu" className="btn-ikona-v-pruhu">
              <Undo2 className="h-5 w-5 sm:h-4 sm:w-4" />
            </button>
          )}
          {/* 6. 10. 2026 (Láďa): hromadná úprava jako výrazné tlačítko s textem, samotný
              čtvereček v hlavičce nebyl vidět. V režimu výběru ukazuje počet vybraných. */}
          {editable && (
            <button
              type="button"
              onClick={() => { if (multiSelectMode) exitMultiSelect(); else setMultiSelectMode(true); }}
              title={multiSelectMode ? "Ukončit hromadnou úpravu" : "Hromadná úprava: označ víc buněk najednou a nastav jim Volno, Oběd nebo Mimo"}
              aria-label="Hromadná úprava"
              aria-pressed={multiSelectMode}
              className={`inline-flex min-h-[36px] shrink-0 items-center gap-1.5 rounded-full px-3 text-[13px] font-bold transition ${multiSelectMode ? "bg-white text-brand-deep shadow-sm" : "border border-white/40 bg-white/15 text-white hover:bg-white/25"}`}
            >
              {multiSelectMode ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
              <span>{multiSelectMode ? `Vybráno: ${selectedCells.size}` : "Hromadná úprava"}</span>
            </button>
          )}
          <button onClick={onRefresh} disabled={refreshing} title="Přepočítat" aria-label="Přepočítat" className="btn-ikona btn-ikona-v-pruhu">
            <RefreshCw className={`h-5 w-5 sm:h-4 sm:w-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </h2>

        {/* Telefon: přepínač dnů a sloty vybraného dne pod sebou (fáze 3, 3.9.2026).
            Dřív se dny přejížděly do strany, jména se sekala a nebylo vidět, kde jsem. */}
        <div className="sm:hidden">
          <div className="mb-3 grid grid-cols-5 gap-1.5">
            {visibleDaysFull.map((d) => {
              const vybrany = d === mobileSelectedDay;
              const obsazeno = hours.filter((h) => {
                const c = cellMap.get(`${d}-${h}`);
                return c && (c.type === "fixed" || c.type === "merged" || c.type === "preliminary");
              }).length;
              return (
                <button
                  key={`den-${d}`}
                  onClick={() => setMobileSelectedDay(d)}
                  aria-pressed={vybrany}
                  className={`flex min-h-[56px] flex-col items-center justify-center rounded-xl border ${
                    vybrany ? "border-2 border-brand bg-brand text-white" : "border-slate-200 bg-white"
                  }`}
                >
                  <span className={`text-xs font-bold ${vybrany ? "text-white/80" : "text-slate-500"}`}>{(DAY_NAMES[d] || "").slice(0, 2)}</span>
                  <span className={`text-base font-bold tabular-nums ${vybrany ? "text-white" : "text-slate-900"}`}>{obsazeno}</span>
                </button>
              );
            })}
          </div>

          <div className="mb-2 flex items-center gap-2.5 rounded-xl bg-brand-100 px-3 py-2">
            <Calendar className="h-4 w-4 shrink-0 text-brand-deep" />
            <span className="min-w-0 flex-1 text-sm font-bold text-brand-950">{DAY_NAMES[mobileSelectedDay] || ""}</span>
          </div>

          <div className="space-y-1.5">
            {hours.map((h) => {
              const d = mobileSelectedDay;
              const cell = cellMap.get(`${d}-${h}`) || { dow: d, hour: h, type: "volno", klienti: [] };
              const roamingHere = roamingByCell.get(`${d}-${h}`) || [];
              const od = `${String(h).padStart(2, "0")}:00`;
              const doHod = `${String(h + 1).padStart(2, "0")}:00`;
              return (
                <div key={`m-${d}-${h}`}>
                  <div className="mb-1 text-sm font-bold tabular-nums text-slate-600">{od} až {doHod}</div>
                  <SlotCell
                    cell={cell}
                    roamingOverlay={roamingHere}
                    editable={editable}
                    selected={selectedCells.has(`${d}-${h}`)}
                    multiSelectMode={multiSelectMode}
                    onClick={() => handleCellClick(cell)}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Mřížka — jen desktop/tablet (sm a výš) */}
        <div className="hidden sm:block mt-4 overflow-x-auto">
          <div className={`grid ${gridColsClass} gap-1.5 ${minWidthClass}`}>
            <div></div>
            {/* Day name header v gridu - na mobile schovaný (chip picker už den ukazuje) */}
            {visibleDays.map((d) => (
              <div key={`h-${d}`} className="hidden sm:block text-center text-sm uppercase tracking-wider font-bold text-slate-600 py-1">
                {DAY_NAMES[d]}
              </div>
            ))}
            {hours.map((h) => {
              // Detekce: všechny visibleDays v této hodině mají oběd? → kompaktní bar.
              // Pokud user rozbalil tuto hodinu (expandedHours), vždy render 5 buněk.
              const allLunchAuto = visibleDays.length > 0 && visibleDays.every((d) => {
                const cell = cellMap.get(`${d}-${h}`);
                return cell && cell.type === "lunch";
              });
              const isExpanded = expandedHours.has(h);
              const allLunch = allLunchAuto && !isExpanded;
              return (
                <React.Fragment key={`row-${h}`}>
                  <div className="text-sm font-bold tabular-nums text-slate-600 py-2 pr-2 text-right self-center flex flex-col items-end gap-1 leading-tight">
                    {/* Mobile: jen "08:00" (úspora místa). Desktop: full "08:00 – 09:00". */}
                    <span className="sm:hidden">{String(h).padStart(2, "0")}:00</span>
                    <span className="hidden sm:inline">{formatHour(h)}</span>
                    {allLunchAuto && isExpanded && (
                      <button
                        type="button"
                        onClick={() => toggleExpandedHour(h)}
                        className="inline-flex items-center justify-center w-5 h-5 rounded text-amber-600 hover:text-white hover:bg-amber-500 border border-amber-300 hover:border-amber-500 transition-colors"
                        title="Sbalit zpět do kompaktního baru obědu"
                      >
                        <ChevronsUp className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {allLunch ? (
                    <div
                      className="bg-amber-100 border border-amber-300 text-amber-800 rounded-xl pl-2.5 pr-1.5 py-1 min-h-[44px] flex items-center gap-2"
                      style={{ gridColumn: `span ${visibleDays.length} / span ${visibleDays.length}` }}
                    >
                      <Coffee className="w-4 h-4 shrink-0" />
                      <span className="text-sm font-bold uppercase tracking-wide">Oběd</span>
                      <span className="hidden sm:inline-flex items-center gap-2 text-xs text-amber-700/80"><span className="w-px h-3.5 bg-amber-300" aria-hidden="true" />Po až Pá</span>
                      {/* 7. 10. 2026 (Láďa): místo šedého textu „klikni pro rozbalení" pořádné tlačítko. */}
                      {!jenCteni && <button
                        type="button"
                        onClick={() => toggleExpandedHour(h)}
                        className="ml-auto inline-flex items-center rounded-full bg-white border border-amber-300 text-amber-800 text-xs font-semibold px-3 py-1.5 shadow-sm hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-colors"
                        title="Rozdělit oběd po dnech a upravit jednotlivé dny"
                      >
                        Rozdělit
                      </button>}
                    </div>
                  ) : (
                    visibleDays.map((d) => {
                      const cell = cellMap.get(`${d}-${h}`) || { dow: d, hour: h, type: "volno", klienti: [] };
                      const selected = selectedCells.has(`${d}-${h}`);
                      const roamingHere = roamingByCell.get(`${d}-${h}`) || [];
                      return <SlotCell
                        key={`c-${d}-${h}`}
                        cell={cell}
                        roamingOverlay={roamingHere}
                        editable={editable}
                        selected={selected}
                        multiSelectMode={multiSelectMode}
                        onClick={() => handleCellClick(cell)}
                      />;
                    })
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-3 text-sm text-slate-600 flex-wrap">
          <LegendChip color="bg-brand-100 border-brand-300" label="Pevný klient (z analýzy)" />
          <LegendChip color="bg-brand-200 border-brand-500" label="Pevný klient (ručně)" />
          <LegendChip color="bg-blue-50 border-blue-300" label="Předrezervace (čeká na trial)" />
          <LegendChip color="bg-emerald-100 border-emerald-300" label="Spojený trénink" />
          <LegendChip color="bg-amber-100 border-amber-300" label="Oběd" />
          <LegendChip color="bg-slate-50 border-slate-200" label="Volný slot" />
          <LegendChip color="bg-slate-50 border-slate-200" label="Volný slot, putující" textColor="text-cyan-700" />
          <LegendChip color="bg-zinc-200 border-zinc-400" label="Nepracuje" customStyle={{
            backgroundImage:
              "repeating-linear-gradient(135deg, rgba(82,82,91,0.25) 0, rgba(82,82,91,0.25) 3px, transparent 3px, transparent 6px)",
          }} />
          {editable && multiSelectMode && <span className="text-brand-600 font-semibold">Klikni na buňky pro výběr</span>}
        </div>

        {/* Skryté dny - možnost vrátit zpět */}
        {hiddenDays.length > 0 && (
          <div className="mt-2 flex items-center justify-between gap-2 text-sm px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl">
            <div className="text-slate-700">
              {hiddenDays.length === 1 ? "Skrytý den:" : "Skryté dny:"}{" "}
              <span className="font-semibold text-slate-800">
                {hiddenDays.map((d) => DAY_NAMES[d]).join(", ")}
              </span>
              {" "}<span className="text-slate-500">(trenér nepracuje)</span>
            </div>
            <button
              onClick={() => setShowHiddenDays(!showHiddenDays)}
              className="text-brand-700 hover:text-brand-800 font-semibold underline"
            >
              {showHiddenDays ? "Skrýt zpět" : "Zobrazit"}
            </button>
          </div>
        )}
      </div>

      {/* Pravý sloupec - putující klienti (jen pokud existují) */}
      {hasRoaming && (
        <div className="min-w-0 space-y-3 2xl:sticky 2xl:top-4 2xl:self-start 2xl:max-h-[calc(100vh-2rem)] 2xl:overflow-y-auto">
          {roaming.length > 0 && <RoamingList
            roaming={roaming}
            title="Putující se vzorem"
            tip="Klienti, kteří chodí pravidelně, ale nemají jeden pevný čas. Algoritmus detekuje vzor: stejný den (např. út, čt), stejná hodina (např. vždy v 15:00) nebo denní okno (dopoledne / odpoledne) v ≥ 50 % bookings. U každého vidíš odhad četnosti za týden."
          />}
          {sparse.length > 0 && <RoamingList
            roaming={sparse}
            title="Bez pevného vzoru"
            tip="Klienti chodí, ale rozvrh je úplně rozhozený - žádný den, čas ani okno netvoří většinu. Sales s nimi nemůže snadno počítat při plánování nového klienta."
          />}
        </div>
      )}
      </div>

      {/* Kapacita souhrn - full width pod */}
      <KapacitaSouhrn capacity={capacity} />

      {/* Plánované absence (dovolená, nemoc) - full width pod */}
      <AbsenceSection
        absences={absences}
        editable={canEditAbsence}
        onAdd={() => setShowAbsenceModal(true)}
        onDelete={onAbsenceDelete}
      />

      {summary && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-slate-700">
          <span className="font-bold">{summary.totalBookings}</span> tréninků za posledních {window?.weeks} týdnů
          {summary.fixedClientsCount > 0 && <>, <span className="font-bold">{summary.fixedClientsCount}</span> pevných</>}
          {summary.roamingCount > 0 && <>, <span className="font-bold">{summary.roamingCount}</span> putujících se vzorem</>}
          {summary.sparseCount > 0 && <>, <span className="font-bold">{summary.sparseCount}</span> bez vzoru</>}
        </div>
      )}

      {/* Multi-select footer */}
      {editable && multiSelectMode && selectedCells.size > 0 && (
        <BulkEditFooter
          count={selectedCells.size}
          onAction={handleBulkAction}
          onReset={handleBulkReset}
          onCancel={exitMultiSelect}
        />
      )}

      {/* Single edit modal - fire-and-forget: zavřeme modal okamžitě, POST běží na pozadí */}
      {editingCell && (
        <EditCellModal
          cell={editingCell}
          canFixClient={canFixClient}
          activeClients={activeClients}
          onClose={() => setEditingCell(null)}
          onSave={(newType, klient, trialDate) => {
            const c = editingCell;
            setEditingCell(null);
            // Optimistic UI v parent → grid se mění hned, POST jen aktualizuje sheet
            Promise.resolve(onCellEdit(c, newType, klient, trialDate)).catch(() => {});
          }}
          onReset={() => {
            const c = editingCell;
            setEditingCell(null);
            Promise.resolve(onCellReset(c)).catch(() => {});
          }}
        />
      )}

      {/* Absence modal - taky fire-and-forget */}
      {showAbsenceModal && (
        <AbsenceModal
          canBackdate={canBackdateAbsence}
          minDaysAhead={minAbsenceDaysAhead}
          onClose={() => setShowAbsenceModal(false)}
          onSave={(payload) => {
            setShowAbsenceModal(false);
            Promise.resolve(onAbsenceAdd(payload)).catch(() => {});
          }}
        />
      )}
    </div>
  );
}

function ClientLink({ name, className = "", color = "text-brand-900", isCizi = false, pilotName = "" }) {
  // Klikací jméno klienta → /trener/klienti?q=Jméno (pre-fill search v Klienti listu)
  const href = `/trener/klienti?q=${encodeURIComponent(name || "")}`;
  const title = isCizi
    ? `${name} - cizí klient (oficiální coach: ${pilotName || "neznámý"}). Trénoval ho mimo svůj pilot.`
    : `Otevřít profil: ${name}`;
  return (
    <span className="inline-flex items-center gap-1 max-w-full">
      <Link
        to={href}
        onClick={(e) => e.stopPropagation()}
        className={`text-sm font-semibold demo-sensitive truncate hover:underline ${color} ${className}`}
        title={title}
      >
        {name}
      </Link>
      {isCizi && (
        <span
          className="text-[11px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 flex-shrink-0"
          title={`Cizí klient - oficiální coach: ${pilotName || "?"}`}
        >
          cizí
        </span>
      )}
    </span>
  );
}

function SlotCell({ cell, roamingOverlay = [], editable, selected, multiSelectMode, onClick }) {
  const { type, klienti = [], ciziKlienti = [], pilotNames = [], poznamka, overridden } = cell;
  let content, bg, border, textColor, customStyle = null;
  // Rozbalení skrytých putujících klientů ve volné buňce (defaultně 2 viditelní + "+N").
  const [showAllRoamers, setShowAllRoamers] = useState(false);

  if (type === "fixed") {
    // Rozlišujeme analýzu (auto z reálných tréninků) vs manuálně přidaný pevný (overridden=true).
    // Manuálně: tmavší violet bg + tečkovaný (dashed) border + badge "Ručně" nad jménem.
    if (overridden) {
      bg = "bg-brand-100"; border = "border-brand-500 border-dashed border-2"; textColor = "text-brand-900";
      content = (
        <div className="space-y-1">
          {/* Badge na samostatném řádku nahoře (analogie předrezervace), jméno klienta dole */}
          <div>
            <span className="inline-flex items-center gap-0.5 text-[11px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-brand-700 text-white">
              <Pin className="w-2.5 h-2.5" />
              <span>Ručně</span>
            </span>
          </div>
          <ClientLink
            name={klienti[0]}
            color="text-brand-900 hover:text-brand-950"
            isCizi={ciziKlienti[0]}
            pilotName={pilotNames[0]}
          />
        </div>
      );
    } else {
      // Pevný klient z analýzy (algoritmus zachytil pravidelný vzor)
      bg = "bg-brand-100"; border = "border-brand-300"; textColor = "text-brand-900";
      content = <ClientLink
        name={klienti[0]}
        color="text-brand-900 hover:text-brand-950"
        isCizi={ciziKlienti[0]}
        pilotName={pilotNames[0]}
      />;
    }
  } else if (type === "merged") {
    bg = "bg-emerald-100"; border = "border-emerald-300"; textColor = "text-emerald-900";
    content = (
      <div className="space-y-0.5">
        <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700">Spojený</div>
        {klienti.map((k, i) => (
          <div key={i}>
            <ClientLink
              name={k}
              color="text-emerald-900 hover:text-emerald-950"
              isCizi={ciziKlienti[i]}
              pilotName={pilotNames[i]}
            />
          </div>
        ))}
      </div>
    );
  } else if (type === "preliminary") {
    // Předrezervace - sales si nárokuje slot pro klienta čekajícího na trial.
    // Modrá barva (mezi violet pevný a slate volno). Layout: malý badge nahoře + jméno + datum trialu.
    bg = "bg-blue-50"; border = "border-blue-300"; textColor = "text-blue-900";
    const trialDate = cell.preliminaryTrialDate || "";
    const fmtDate = (iso) => {
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? `${parseInt(m[3], 10)}.${parseInt(m[2], 10)}.` : iso;
    };
    content = (
      <div className="space-y-1">
        {/* Badge na samostatném řádku, datum trialu pod ním, jméno klienta dole */}
        <div>
          <span className="inline-flex items-center gap-0.5 text-[11px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-blue-200 text-blue-800">
            <span>⏳</span>
            <span>Předrezervace</span>
          </span>
        </div>
        {trialDate && (
          <div className="text-[11px] text-blue-700 font-semibold whitespace-nowrap">
            trial {fmtDate(trialDate)}
          </div>
        )}
        <ClientLink
          name={klienti[0]}
          color="text-blue-900 hover:text-blue-950"
        />
      </div>
    );
  } else if (type === "lunch") {
    bg = "bg-amber-100"; border = "border-amber-300"; textColor = "text-amber-800";
    content = <div className="flex items-center gap-1.5 justify-center"><Coffee className="w-4 h-4" /><span className="text-sm font-bold">Oběd</span></div>;
  } else if (type === "hidden") {
    bg = "bg-zinc-100"; border = "border-zinc-400"; textColor = "text-zinc-700";
    customStyle = {
      backgroundImage:
        "repeating-linear-gradient(135deg, rgba(82,82,91,0.18) 0, rgba(82,82,91,0.18) 7px, transparent 7px, transparent 14px)",
    };
    content = (
      <div className="flex items-center gap-1.5 justify-center">
        <EyeOff className="w-4 h-4" />
        <span className="text-sm font-bold uppercase tracking-wide">Nepracuje</span>
      </div>
    );
  } else {
    bg = "bg-slate-50"; border = "border-slate-200 border-dashed"; textColor = "text-slate-500";
    if (roamingOverlay.length > 0) {
      // Volno + putující overlay (max 2 nejsilnější) - Sales vidí "tady cca chodí X"
      content = (
        <div className="space-y-0.5">
          <div className="text-center text-xs italic text-slate-500">Volný slot</div>
          <div className="border-t border-slate-200 pt-1 space-y-0.5">
            {(showAllRoamers ? roamingOverlay : roamingOverlay.slice(0, 2)).map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-1">
                <span className="text-xs font-semibold text-sky-700 demo-sensitive truncate" title={`${r.klient} - chodí sem ${Math.round(r.ratio * 100)} % svých tréninků`}>
                  ~ {r.klient}
                </span>
                <span className="text-[11px] text-sky-500 tabular-nums whitespace-nowrap font-semibold">
                  {Math.round(r.ratio * 100)}%
                </span>
              </div>
            ))}
            {roamingOverlay.length > 2 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowAllRoamers((v) => !v); }}
                className="w-full mt-1 py-1.5 text-xs font-bold text-sky-700 hover:text-sky-900 text-center rounded-lg bg-sky-50 hover:bg-sky-100 active:bg-sky-200 border border-sky-200 cursor-pointer transition-colors"
              >
                {showAllRoamers ? "skrýt" : `+ ${roamingOverlay.length - 2} další`}
              </button>
            )}
          </div>
        </div>
      );
    } else {
      content = <div className="text-center text-sm italic">Volný slot</div>;
    }
  }

  // 24.8.2026 (hlášení z /feedback): klikací je KAŽDÁ buňka, i ta, kde analýza
  // našla pevného klienta. Dřív šlo editovat jen to, co bylo nastavené ručně,
  // takže když trenér přestal v daném čase pracovat, nedalo se to vůbec zadat
  // a slot se pak choval podle docházky. Přepnutí na Nepracuje / Volno / Oběd
  // navíc trenérovi založí úkol klienta přeobjednat.
  const isClickable = editable;
  const editableClass = isClickable ? (multiSelectMode ? "cursor-pointer hover:ring-2 hover:ring-brand-200" : "cursor-pointer hover:ring-2 hover:ring-brand-300") : "";
  const selectedClass = selected ? "ring-2 ring-brand-500 bg-brand-50" : "";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isClickable}
      style={customStyle || undefined}
      className={`${selected ? "bg-brand-50 border-brand-400" : `${bg} ${border}`} ${textColor} ${editableClass} ${selectedClass} w-full border rounded-lg p-2 min-h-[60px] text-left transition-all relative`}
      title={poznamka || (overridden ? "Manuálně upraveno" : "")}
    >
      {content}
      {/* Pin badge u manuálního pevného klienta je teď přímo v contentu (viditelnější).
          Pro volno/lunch/hidden override žádný indikátor - chová se jako čistý stav. */}
      {selected && (
        <div className="absolute top-0.5 right-0.5"><CheckSquare className="w-3 h-3 text-brand-700" /></div>
      )}
    </button>
  );
}

function KapacitaSouhrn({ capacity }) {
  const { totalVolno, morningVolno, afternoonVolno, roamingExpected, remainingForSales, totalFixed, totalLunch, totalHidden } = capacity;
  return (
    <div className="bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-200 rounded-xl p-4">
      <div className="text-sm font-bold text-slate-800 mb-3 inline-flex items-center gap-2">
        <Calendar className="w-4 h-4 text-sky-700" />
        Volná kapacita / týden
        <InfoTip text="Souhrn volných slotů v týdenním vzoru. Pomáhá Sales rychle vidět kolik je v rozvrhu prostoru pro nové klienty. Obsazeno putujícími = odhad kolik volných slotů zaberou klienti bez fixního času (suma jejich průměrných tréninků týdně). Volné sloty pro sales = volných slotů celkem mínus obsazeno putujícími." />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <CapTile
          icon={Sun}
          label="Dopoledne"
          value={morningVolno}
          subtitle="volných slotů (před 12:00)"
          accent="amber"
          tip="Počet volných slotů v dopoledních hodinách (před 12:00) napříč Po až Pá. Nezahrnuje oběd, pevné klienty ani Nepracuje sloty."
        />
        <CapTile
          icon={Moon}
          label="Odpoledne"
          value={afternoonVolno}
          subtitle="volných slotů (od 14:00)"
          accent="violet"
          tip="Počet volných slotů odpoledne (od 14:00) napříč Po až Pá. Nezahrnuje oběd, pevné klienty ani Nepracuje sloty."
        />
        <CapTile
          icon={Users}
          label="Obsazeno putujícími"
          value={`~${roamingExpected}`}
          subtitle="očekávaná spotřeba volných slotů"
          accent="sky"
          tip="Suma průměrných týdenních tréninků všech putujících klientů (s vzorem i bez). Tito klienti nemají pevný slot, ale chodí pravidelně. Odhad kolik volných slotů týdně reálně zaberou."
        />
        <CapTile
          icon={Calendar}
          label="Volné sloty pro sales"
          value={`~${remainingForSales}`}
          subtitle="po odečtu putujících"
          accent="emerald"
          tip="Hrubý odhad kolik slotů týdně může Sales nabídnout novým klientům na zkušební trénink. Vzorec: volné sloty celkem mínus očekávaná spotřeba putujícími. Pokud je nízké, trenér je plně vytížen."
        />
      </div>
      <div className="mt-3 text-sm text-slate-600">
        Celkem v rozvrhu: <strong>{totalFixed}</strong> obsazeno, <strong>{totalLunch}</strong> oběd, <strong>{totalVolno}</strong> volných slotů{totalHidden > 0 && <>, <strong>{totalHidden}</strong> mimo</>}
      </div>
    </div>
  );
}

function CapTile({ icon: Icon, label, value, subtitle, accent, tip }) {
  const map = {
    amber:   { bg: "bg-amber-50",   border: "border-amber-200",   icon: "bg-amber-100   text-amber-700",   value: "text-amber-800" },
    violet:  { bg: "bg-brand-50",  border: "border-brand-200",  icon: "bg-brand-100  text-brand-700",  value: "text-brand-800" },
    sky:     { bg: "bg-sky-50",     border: "border-sky-200",     icon: "bg-sky-100     text-sky-700",     value: "text-sky-800" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "bg-emerald-100 text-emerald-700", value: "text-emerald-800" },
  };
  const a = map[accent];
  return (
    <div className={`${a.bg} border ${a.border} rounded-xl p-3`}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-7 h-7 rounded-lg ${a.icon} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-700 font-bold leading-tight inline-flex items-center gap-1">
          {label}
          {tip && <InfoTip text={tip} />}
        </div>
      </div>
      <div className={`text-2xl font-black tabular-nums ${a.value} leading-tight`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
    </div>
  );
}

function LegendChip({ color, label, customStyle }) {
  return (
    <div className="inline-flex items-center gap-1">
      <span className={`inline-block w-3 h-3 rounded ${color} border`} style={customStyle || undefined}></span>
      <span>{label}</span>
    </div>
  );
}

function RoamingList({ roaming, title, tip }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
          <Users className="w-4 h-4" />
        </div>
        <div>
          <div className="text-base font-bold text-slate-800 inline-flex items-center gap-1.5">
            {title}
            {tip && <InfoTip text={tip} />}
          </div>
          <div className="text-sm text-slate-500">{roaming.length} {roaming.length === 1 ? "klient" : roaming.length < 5 ? "klienti" : "klientů"}</div>
        </div>
      </div>
      <div className="space-y-1.5">
        {roaming.map((r, i) => {
          const href = `/trener/klienti?q=${encodeURIComponent(r.klient || "")}`;
          const title = r.isCizi
            ? `${r.klient} - cizí klient (oficiální coach: ${r.pilotName || "?"})`
            : `Otevřít profil: ${r.klient}`;
          return (
            <Link
              key={i}
              to={href}
              className="block bg-sky-50 border border-sky-200 hover:bg-sky-100 hover:border-sky-300 rounded-xl p-2.5 transition-colors"
              title={title}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="inline-flex items-center gap-1.5 max-w-full">
                    <span className="text-base font-semibold text-slate-800 demo-sensitive truncate hover:text-sky-800">
                      {r.klient}
                    </span>
                    {r.isCizi && (
                      <span className="text-[11px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 flex-shrink-0">
                        cizí
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500 mt-0.5">
                    {r.note}
                    {r.isCizi && r.pilotName && <span className="ml-1 text-amber-700">, coach {r.pilotName}</span>}
                  </div>
                </div>
                {r.tydneFreq != null && (
                  <div className="text-xs uppercase tracking-wider text-sky-700 font-bold whitespace-nowrap flex-shrink-0">
                    {r.tydneFreq}× / týden
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function BulkEditFooter({ count, onAction, onReset, onCancel }) {
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-white border border-brand-300 shadow-2xl rounded-xl p-3 flex items-center gap-2 max-w-[95vw] flex-wrap">
      <div className="text-sm font-bold text-brand-700 px-2">
        {count} {count === 1 ? "slot" : count < 5 ? "sloty" : "slotů"} vybráno
      </div>
      <div className="flex items-center gap-1.5">
        <button onClick={() => onAction("volno")} className="text-xs font-semibold px-3 h-8 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300">
          Volný slot
        </button>
        <button onClick={() => onAction("lunch")} className="text-xs font-semibold px-3 h-8 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 inline-flex items-center gap-1">
          <Coffee className="w-3.5 h-3.5" /> Oběd
        </button>
        <button onClick={() => onAction("hidden")} className="text-xs font-semibold px-3 h-8 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 border border-slate-400 inline-flex items-center gap-1">
          <EyeOff className="w-3.5 h-3.5" /> Mimo
        </button>
        <button onClick={onReset} className="text-xs font-semibold px-3 h-8 rounded-xl bg-white hover:bg-slate-50 text-slate-600 border border-slate-300">
          Reset na auto
        </button>
        <button onClick={onCancel} className="text-xs font-semibold px-2 h-8 rounded-xl bg-white hover:bg-rose-50 text-rose-600 border border-rose-200 inline-flex items-center">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function EditCellModal({ cell, canFixClient = false, activeClients = [], onClose, onSave, onReset }) {
  const HOUR_LABEL = `${String(cell.hour).padStart(2, "0")}:00`;
  const DAY_LABEL = DAY_NAMES[cell.dow];
  // Mode: null = main menu, "fixed" = picker pro pevného, "preliminary" = picker + datum trialu pro předrezervaci
  const [pickingMode, setPickingMode] = useState(null);
  const [clientQuery, setClientQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  // Default trial datum = nejbližší výskyt dnu+hodiny slotu (např. nejbližší středa 18:00)
  const defaultTrialDate = useMemo(() => {
    const today = new Date();
    const todayDow = today.getDay() === 0 ? 7 : today.getDay(); // Mon=1..Sun=7, ale cell.dow Mon=1..Fri=5
    let daysAhead = (cell.dow - todayDow + 7) % 7;
    if (daysAhead === 0) daysAhead = 7; // pokud je dnes ten den, vezmi za týden
    const trialDay = new Date(today); trialDay.setDate(trialDay.getDate() + daysAhead);
    return `${trialDay.getFullYear()}-${String(trialDay.getMonth() + 1).padStart(2, "0")}-${String(trialDay.getDate()).padStart(2, "0")}`;
  }, [cell.dow]);
  const [trialDate, setTrialDate] = useState(defaultTrialDate);

  const filteredClients = useMemo(() => {
    if (!clientQuery.trim()) return activeClients.slice(0, 8);
    const q = clientQuery.toLowerCase();
    return activeClients.filter((c) => (c.name || c).toLowerCase().includes(q)).slice(0, 8);
  }, [clientQuery, activeClients]);

  if (pickingMode) {
    const isPreliminary = pickingMode === "preliminary";
    const accent = isPreliminary
      ? { headerColor: "text-blue-700", borderActive: "border-blue-200", bgActive: "bg-blue-50", hoverBg: "hover:bg-blue-100", hoverBorder: "hover:border-blue-300", focusBorder: "focus:border-blue-500", focusRing: "focus:ring-blue-500" }
      : { headerColor: "text-brand-700", borderActive: "border-brand-200", bgActive: "bg-brand-50", hoverBg: "hover:bg-brand-100", hoverBorder: "hover:border-brand-300", focusBorder: "focus:border-brand-500", focusRing: "focus:ring-brand-500" };
    const showDatePicker = isPreliminary && selectedClient;
    return (
      <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
        <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
          <div className={`text-base font-bold mb-1 inline-flex items-center gap-1.5 ${accent.headerColor}`}>
            {isPreliminary ? <span>⏳</span> : <Pin className="w-4 h-4" />}
            {isPreliminary ? "Předrezervace termínu" : "Pevný klient"}
          </div>
          <div className="text-sm text-slate-600 mb-3">{DAY_LABEL}, {HOUR_LABEL}</div>
          {!showDatePicker && (
            <>
              <input
                type="text"
                autoFocus
                placeholder="Hledat klienta..."
                value={clientQuery}
                onChange={(e) => setClientQuery(e.target.value)}
                className={`w-full px-3 py-2 text-sm border border-slate-300 rounded-xl ${accent.focusBorder} focus:ring-1 ${accent.focusRing} outline-none mb-2`}
              />
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {filteredClients.length === 0 && <div className="text-sm text-slate-400 italic p-2">Žádný klient nenalezen</div>}
                {filteredClients.map((c, i) => {
                  const name = c.name || c;
                  const isCizi = !!c.isCizi;
                  const oficiCoach = c.trener || "";
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        if (isPreliminary) {
                          setSelectedClient(name); // přejdi na date picker
                        } else {
                          onSave("fixed", name);
                        }
                      }}
                      className={`w-full text-left px-3 py-2 text-sm rounded-xl border demo-sensitive transition-colors ${
                        isCizi
                          ? "border-amber-200 bg-amber-50 hover:bg-amber-100 hover:border-amber-300"
                          : `${accent.borderActive} ${accent.bgActive} ${accent.hoverBg} ${accent.hoverBorder}`
                      }`}
                      title={isCizi ? `Cizí klient. Oficiální coach: ${oficiCoach || "?"}` : undefined}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold">{name}</span>
                        {isCizi && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded-full border border-amber-300 bg-amber-100 text-amber-800 font-bold uppercase tracking-wider">cizí</span>
                        )}
                      </div>
                      {isCizi && oficiCoach && (
                        <div className="text-xs text-amber-700 mt-0.5">coach: {oficiCoach}</div>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 text-[11px] text-slate-500 leading-snug">
                {isPreliminary
                  ? "Tip: předrezervace drží slot dokud se klient nerozhodne. Po proběhnutí trial tréninku ti přijde notifikace, abys ho potvrdil jako pevného klienta."
                  : "Tip: pevný klient je dočasná pečeť. Jakmile algoritmus zachytí pravidelný vzor v tréninkách, přepíše ho automaticky podle reálné docházky."}
              </div>
            </>
          )}
          {showDatePicker && (
            <div>
              <div className={`mb-3 px-3 py-2 rounded-xl ${accent.bgActive} border ${accent.borderActive}`}>
                <div className="text-xs text-slate-600">Klient</div>
                <div className="text-sm font-semibold text-slate-900 demo-sensitive">{selectedClient}</div>
              </div>
              <label className="block">
                <div className="text-xs font-semibold text-slate-700 mb-1">Datum zkušebního tréninku</div>
                <input
                  type="date"
                  value={trialDate}
                  onChange={(e) => setTrialDate(e.target.value)}
                  className={`w-full px-3 py-2 text-sm border border-slate-300 rounded-xl ${accent.focusBorder} focus:ring-1 ${accent.focusRing} outline-none`}
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Den po proběhlém trialu předrezervace zmizí, pokud nebyl trénink absolvován.
                </div>
              </label>
              <button
                onClick={() => onSave("preliminary", selectedClient, trialDate)}
                disabled={!trialDate}
                className="btn btn-primary w-full mt-3"
              >
                Vytvořit předrezervaci
              </button>
              <button
                onClick={() => { setSelectedClient(""); setClientQuery(""); }}
                className="w-full mt-2 text-xs text-slate-500 hover:text-slate-700"
              >
                Vybrat jiného klienta
              </button>
            </div>
          )}
          <button onClick={() => { setPickingMode(null); setSelectedClient(""); setClientQuery(""); }} className="w-full mt-3 text-xs text-slate-500 hover:text-slate-700">Zpět na úpravu slotu</button>
        </div>
      </div>
    );
  }

  // Detekce: aktuální slot je předrezervace? Pak ukážeme speciální banner + tlačítko Smazat
  const isExistingPreliminary = cell.type === "preliminary" && cell.overridden;
  // Manuálně přidaný pevný klient (override fixed) - taky speciální banner
  const isExistingManualFixed = cell.type === "fixed" && cell.overridden;
  const fmtDate = (iso) => {
    if (!iso) return "";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${parseInt(m[3], 10)}.${parseInt(m[2], 10)}.${m[1]}` : iso;
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="text-base font-bold text-slate-800 mb-1">Úprava slotu</div>
        <div className="text-sm text-slate-600 mb-4">{DAY_LABEL}, {HOUR_LABEL}</div>
        {/* Pokud je slot existující předrezervace, ukáž info + výrazné tlačítko Smazat */}
        {isExistingPreliminary && (
          <div className="mb-4 p-3 rounded-xl bg-blue-50 border border-blue-300">
            <div className="text-xs uppercase tracking-wider font-bold text-blue-700 inline-flex items-center gap-1 mb-1">
              <span>⏳</span><span>Aktuální předrezervace</span>
            </div>
            <div className="text-sm font-semibold text-blue-900 demo-sensitive">{(cell.klienti && cell.klienti[0]) || ""}</div>
            {cell.preliminaryTrialDate && (
              <div className="text-xs text-blue-700 mt-0.5">Plánovaný trial: {fmtDate(cell.preliminaryTrialDate)}</div>
            )}
            <button
              onClick={onReset}
              className="btn btn-danger w-full mt-2.5"
              title="Smazat předrezervaci a vrátit slot na automaticky vypočítaný"
            >
              <X className="w-3.5 h-3.5" /> Smazat předrezervaci
            </button>
          </div>
        )}
        {/* Pokud je slot manuálně přidaný pevný klient - info + tlačítko Smazat */}
        {isExistingManualFixed && (
          <div className="mb-4 p-3 rounded-xl bg-brand-50 border border-brand-300">
            <div className="text-xs uppercase tracking-wider font-bold text-brand-700 inline-flex items-center gap-1 mb-1">
              <Pin className="w-3 h-3" /><span>Pevný klient (ručně)</span>
            </div>
            <div className="text-sm font-semibold text-brand-900 demo-sensitive">{(cell.klienti && cell.klienti[0]) || ""}</div>
            <div className="text-xs text-brand-700 mt-0.5">
              Naseedovaný ručně. Algoritmus ho převezme automaticky až zachytí pravidelný vzor.
            </div>
            <button
              onClick={onReset}
              className="btn btn-danger w-full mt-2.5"
              title="Smazat manuálního pevného klienta a vrátit slot na automaticky vypočítaný"
            >
              <X className="w-3.5 h-3.5" /> Smazat pevného klienta
            </button>
          </div>
        )}
        <div className="space-y-2">
          {canFixClient && (
            <button onClick={() => setPickingMode("fixed")} className="w-full text-left px-3 py-2.5 rounded-xl border border-brand-200 hover:bg-brand-50 hover:border-brand-300 transition-colors">
              <div className="text-sm font-semibold text-brand-800 inline-flex items-center gap-1.5"><Pin className="w-4 h-4" /> Pevný klient</div>
              <div className="text-xs text-slate-500">Naseeduj klienta do tohoto slotu (vybírej z celé databáze aktivních klientů). Až algoritmus zachytí pravidelný vzor, automaticky přepíše tento záznam reálnou docházkou. Jen owner, manager nebo head coach.</div>
            </button>
          )}
          {/* Předrezervace - sales/management. Klient čekající na trial trénink, drží slot dokud se nerozhodne. */}
          <button onClick={() => setPickingMode("preliminary")} className="w-full text-left px-3 py-2.5 rounded-xl border border-blue-200 hover:bg-blue-50 hover:border-blue-300 transition-colors">
            <div className="text-sm font-semibold text-blue-800 inline-flex items-center gap-1.5"><span>⏳</span> {isExistingPreliminary ? "Změnit předrezervaci (jiný klient nebo datum)" : "Předrezervace termínu"}</div>
            <div className="text-xs text-slate-500">Sales si nárokuje slot pro klienta, který má před sebou zkušební trénink, ale ještě se nerozhodl. Po proběhlém trialu management dostane upozornění aby potvrdil pevného klienta.</div>
          </button>
          <button onClick={() => onSave("volno")} className="w-full text-left px-3 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-colors">
            <div className="text-sm font-semibold text-slate-800">Volný slot</div>
            <div className="text-xs text-slate-500">Sales může nabídnout zkušební trénink</div>
          </button>
          <button onClick={() => onSave("lunch")} className="w-full text-left px-3 py-2.5 rounded-xl border border-amber-200 hover:bg-amber-50 hover:border-amber-300 transition-colors">
            <div className="text-sm font-semibold text-amber-800 inline-flex items-center gap-1.5"><Coffee className="w-4 h-4" /> Oběd / pauza</div>
            <div className="text-xs text-slate-500">Vyhrazený čas, sales nenabízí</div>
          </button>
          <button onClick={() => onSave("hidden")} className="w-full text-left px-3 py-2.5 rounded-xl border border-zinc-300 hover:bg-zinc-100 hover:border-zinc-400 transition-colors">
            <div className="text-sm font-semibold text-zinc-700 inline-flex items-center gap-1.5"><EyeOff className="w-4 h-4" /> Nepracuje (mimo)</div>
            <div className="text-xs text-slate-500">Trenér v této době nepracuje. Sales nenabízí. Pokud celý den, sloupec se skryje.</div>
          </button>
        </div>
        {/* Reset na auto - jen pro overridden sloty co nemají vlastní banner s tlačítkem Smazat (preliminary, manuální fixed). */}
        {cell.overridden && !isExistingPreliminary && !isExistingManualFixed && (
          <button onClick={onReset} className="w-full mt-3 text-xs text-slate-500 hover:text-brand-700 underline transition-colors">
            Vrátit na automaticky vypočítané
          </button>
        )}
        <button onClick={onClose} className="w-full mt-3 text-xs text-slate-500 hover:text-slate-700 transition-colors">Zrušit</button>
      </div>
    </div>
  );
}

// === Plánované absence (dovolená, nemoc, školení) ===
const REASON_LABEL = { dovolena: "Dovolená", nemoc: "Nemoc", skoleni: "Školení", jine: "Jiné" };
const REASON_COLOR = {
  dovolena: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-800" },
  nemoc:    { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-800" },
  skoleni:  { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800" },
  jine:     { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700" },
};

function fmtDateCz(iso) {
  if (!iso) return "-";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${parseInt(m[3], 10)}.${parseInt(m[2], 10)}.${m[1]}` : iso;
}

export function AbsenceSection({ absences, editable, onAdd, onDelete }) {
  const today = new Date().toISOString().substring(0, 10);
  const upcoming = absences.filter((a) => a.dateTo >= today);
  const past = absences.filter((a) => a.dateTo < today);
  const pendingCount = upcoming.filter((a) => (a.status || "approved") === "pending").length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
            <Plane className="w-4 h-4" />
          </div>
          <div>
            <div className="text-base font-bold text-slate-800 inline-flex items-center gap-1.5">
              Plánované absence
              <InfoTip text="Plánované volno trenéra (dovolená, nemoc, školení, jiné). Bookings v období absence se NEzapočítávají do výpočtu týdenního vzoru, aby anomálie nedeformovala detekci pevných klientů. Sales při plánování nového klienta vidí kdy trenér nepracuje." />
            </div>
            <div className="text-sm text-slate-500">
              {upcoming.length === 0 ? "Žádná plánovaná absence" : `${upcoming.length} nadcházející${upcoming.length === 1 ? "" : upcoming.length < 5 ? "" : "ch"}`}
              {pendingCount > 0 && (
                <span className="ml-1 text-amber-700 font-semibold">, {pendingCount} čeká na schválení</span>
              )}
              {past.length > 0 && `, ${past.length} minulých`}
            </div>
          </div>
        </div>
        {editable && (
          <button onClick={onAdd} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 h-11 sm:h-8 rounded-xl bg-sky-600 hover:bg-sky-700 text-white transition-colors">
            <Plus className="w-3.5 h-3.5" /> Přidat
          </button>
        )}
      </div>
      {upcoming.length === 0 && past.length === 0 && (
        <div className="text-xs text-slate-400 italic p-2 text-center">
          {editable ? "Klikni Přidat pro plánované volno (dovolená, nemoc, školení)" : "Žádné plánované absence"}
        </div>
      )}
      <div className="space-y-1.5">
        {upcoming.map((a) => (
          <AbsenceRow key={a.rowNum} absence={a} editable={editable} onDelete={onDelete} isPast={false} />
        ))}
        {past.length > 0 && upcoming.length > 0 && <div className="border-t border-slate-100 my-2"></div>}
        {past.slice(-3).reverse().map((a) => (
          <AbsenceRow key={a.rowNum} absence={a} editable={editable} onDelete={onDelete} isPast={true} />
        ))}
      </div>
    </div>
  );
}

// Status workflow (28.5.2026): hr_absence master.
//   pending  ⇒ čeká na schválení managerem; trenér smí zrušit svoji, jinak zamknuto.
//   approved ⇒ schváleno, pattern výpočet ji bere v potaz.
//   rejected ⇒ zamítnuto; viditelné kvůli důvodu, ale neaktivní.
const STATUS_BADGE = {
  pending:  { label: "Čeká na schválení", bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-300" },
  approved: { label: "Schváleno",         bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300" },
  rejected: { label: "Zamítnuto",         bg: "bg-rose-100",  text: "text-rose-800", border: "border-rose-300" },
};

function AbsenceRow({ absence, editable, onDelete, isPast }) {
  const c = REASON_COLOR[absence.duvod] || REASON_COLOR.jine;
  const label = REASON_LABEL[absence.duvod] || absence.duvod;
  const status = absence.status || "approved"; // legacy bez statusu = approved
  const badge = STATUS_BADGE[status] || STATUS_BADGE.approved;
  const isRejected = status === "rejected";
  // Trenér smí mazat jen pending; manager (canBackdate ⇒ editable + future flag) všechno.
  // Backend stejně ověří, ale tlačítko nezobrazujeme pokud zjevně nelze.
  const canDelete = editable && !isPast && (status !== "approved");

  return (
    <div className={`${c.bg} border ${c.border} rounded-xl p-2.5 flex items-center justify-between gap-2 flex-wrap ${isPast ? "opacity-50" : ""} ${isRejected ? "opacity-60" : ""}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className={`text-xs uppercase tracking-wider font-bold ${c.text} px-2 py-0.5 rounded bg-white/70 border ${c.border} flex-shrink-0`}>
          {label}
        </div>
        <div className={`text-base font-semibold text-slate-800 truncate ${isRejected ? "line-through" : ""}`}>
          {fmtDateCz(absence.dateFrom)}
          {absence.dateFrom !== absence.dateTo && <> – {fmtDateCz(absence.dateTo)}</>}
        </div>
        {absence.poznamka && <div className="text-sm text-slate-500 truncate">{absence.poznamka}</div>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-[11px] uppercase tracking-wider font-bold ${badge.text} ${badge.bg} border ${badge.border} px-2 py-0.5 rounded`}>
          {badge.label}
        </span>
        {isRejected && absence.rejectedReason && (
          <span className="text-xs text-rose-700 italic truncate max-w-[200px]" title={absence.rejectedReason}>
            „{absence.rejectedReason}"
          </span>
        )}
        {canDelete && (
          <button
            onClick={async () => { if (await confirmDialog("Smazat tuto absenci?")) onDelete(absence.rowNum); }}
            className="text-rose-500 hover:text-rose-700 p-1 rounded hover:bg-rose-50 flex-shrink-0"
            title="Smazat"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

const REASON_OPTIONS = [
  { value: "dovolena", label: "Dovolená", color: "sky", emoji: "🏖️" },
  { value: "nemoc",    label: "Nemoc",    color: "rose", emoji: "🤒" },
  { value: "skoleni",  label: "Školení",  color: "amber", emoji: "📚" },
  { value: "jine",     label: "Jiné",     color: "slate", emoji: "📌" },
];

export function AbsenceModal({ canBackdate = false, minDaysAhead = 0, onClose, onSave }) {
  const today = new Date().toISOString().substring(0, 10);
  // Minimální datum pro vstup:
  //   - canBackdate = true (management) → bez omezení (žádný min v inputu)
  //   - canBackdate = false + minDaysAhead = 14 (trener) → today + 14
  const minDateAllowed = useMemo(() => {
    if (canBackdate) return ""; // bez omezení
    if (minDaysAhead > 0) {
      const d = new Date();
      d.setDate(d.getDate() + minDaysAhead);
      return d.toISOString().substring(0, 10);
    }
    return today;
  }, [canBackdate, minDaysAhead, today]);

  const initialDate = canBackdate ? today : (minDateAllowed || today);
  const [dateFrom, setDateFrom] = useState(initialDate);
  const [dateTo, setDateTo] = useState(initialDate);
  const [duvod, setDuvod] = useState("dovolena");
  const [poznamka, setPoznamka] = useState("");

  // Validace
  const errors = useMemo(() => {
    const e = {};
    if (!canBackdate) {
      if (minDaysAhead > 0 && dateFrom < minDateAllowed) {
        e.dateFrom = `Trenér může přidat absenci min. ${minDaysAhead} dní dopředu (od ${fmtDateCz(minDateAllowed)}). Pro dřívější datum kontaktuj manažera.`;
      } else if (minDaysAhead === 0 && dateFrom < today) {
        e.dateFrom = "Datum od nesmí být v minulosti";
      }
    }
    if (dateTo < dateFrom) e.dateTo = "Datum do musí být stejné nebo pozdější než od";
    return e;
  }, [dateFrom, dateTo, today, canBackdate, minDaysAhead, minDateAllowed]);
  const isValid = Object.keys(errors).length === 0;

  // Pokud uživatel změní dateFrom na pozdější než dateTo, auto-posuneme dateTo
  const handleDateFromChange = (newFrom) => {
    setDateFrom(newFrom);
    if (newFrom > dateTo) setDateTo(newFrom);
  };

  const dayCount = useMemo(() => {
    if (!isValid) return 0;
    const d1 = new Date(dateFrom + "T00:00:00");
    const d2 = new Date(dateTo + "T00:00:00");
    return Math.round((d2 - d1) / 86400000) + 1;
  }, [dateFrom, dateTo, isValid]);

  const handleSubmit = () => {
    if (!isValid) return;
    onSave({ dateFrom, dateTo, duvod, poznamka });
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header gradient */}
        <div className="bg-gradient-to-br from-sky-500 to-sky-600 px-5 py-4 text-white">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <Plane className="w-5 h-5" />
            </div>
            <div>
              <div className="text-lg font-bold">Plánované volno</div>
              <div className="text-xs text-sky-100">Bookings v období se nezapočítají do výpočtu</div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Důvod - jako pill picker */}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-2 block">Důvod</label>
            <div className="grid grid-cols-4 gap-2">
              {REASON_OPTIONS.map((opt) => {
                const active = duvod === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setDuvod(opt.value)}
                    className={`px-2 py-2.5 rounded-xl border-2 text-xs font-semibold transition-all ${
                      active
                        ? "bg-sky-600 border-sky-600 text-white shadow-md scale-105"
                        : "bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className="text-lg mb-0.5">{opt.emoji}</div>
                    <div>{opt.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Datumy */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-1 block">Od</label>
              <DatePicker value={dateFrom} min={minDateAllowed || undefined} onChange={handleDateFromChange} />
              {errors.dateFrom && <div className="text-[11px] text-rose-600 mt-1">{errors.dateFrom}</div>}
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-1 block">Do</label>
              <DatePicker value={dateTo} min={dateFrom} onChange={setDateTo} />
              {errors.dateTo && <div className="text-[11px] text-rose-600 mt-1">{errors.dateTo}</div>}
            </div>
          </div>

          {/* Délka */}
          {isValid && (
            <div className="bg-sky-50 border border-sky-200 rounded-xl px-3 py-2 text-xs text-sky-800">
              <span className="font-bold">{dayCount}</span> {dayCount === 1 ? "den" : dayCount < 5 ? "dny" : "dní"} volna
            </div>
          )}

          {/* Poznámka */}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-1 block">Poznámka <span className="text-slate-400 normal-case font-normal">(volitelné)</span></label>
            <input
              type="text"
              value={poznamka}
              onChange={(e) => setPoznamka(e.target.value)}
              placeholder="Např. dovolená v Itálii, lékař, externí workshop"
              className="field w-full"
            />
          </div>

          {/* Pravidlo pro trenéra */}
          {!canBackdate && minDaysAhead > 0 && (
            <div className="bg-brand-50 border border-brand-200 rounded-xl px-3 py-2 text-xs text-brand-800 flex items-start gap-2">
              <span className="text-base">ℹ️</span>
              <div>Trenér může přidat absenci min. <strong>{minDaysAhead} dní dopředu</strong> (od <strong>{fmtDateCz(minDateAllowed)}</strong>). Pro dřívější datum nebo zpětnou změnu kontaktuj manažera nebo head coache.</div>
            </div>
          )}

          {/* Info: manager dostane upozornění */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
            <span className="text-base">🔔</span>
            <div>Po uložení dostane manager upozornění. Schválené volno se odečte z rozvrhu a klientky si na něj termín nevyberou.</div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="text-sm font-semibold px-4 h-10 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Zrušit
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid}
            className="text-sm font-semibold px-5 h-10 rounded-xl bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-md"
          >
            Uložit
          </button>
        </div>
      </div>
    </div>
  );
}
